import { API_ROOT_URL } from '@/lib/constants/api';
import { apiFetch } from '@/lib/utils/apiFetch';
import type {
  AdminRiskPayload,
  AdminRisksResponse,
  RiskGrossPayload,
  RiskGrossResponse,
} from './types';

/**
 * The Retraced-only side of risk analysis: the risk catalogue every assessment
 * scores against, and the gross index held per risk × country.
 *
 * All of it — risks and risk gross — is on the ported backend routes under
 * `/api`.
 */

// ─── Risks ───────────────────────────────────────────────────────────────────

export async function fetchAdminRisks(qs: string): Promise<AdminRisksResponse> {
  return apiFetch<AdminRisksResponse>(
    `/api/risk-analysis/risks?${qs}`,
    {},
    API_ROOT_URL,
  );
}

export async function createAdminRisk(
  payload: AdminRiskPayload,
): Promise<void> {
  await apiFetch<void>(
    '/api/risk-analysis/risks',
    { method: 'POST', body: JSON.stringify(payload) },
    API_ROOT_URL,
  );
}

export async function updateAdminRisk(
  id: string,
  payload: AdminRiskPayload,
): Promise<void> {
  await apiFetch<void>(
    `/api/risk-analysis/risks/${id}`,
    { method: 'PATCH', body: JSON.stringify(payload) },
    API_ROOT_URL,
  );
}

// ─── Risk Gross ──────────────────────────────────────────────────────────────

/**
 * Both the current list and the history behind one row — the history is the
 * same endpoint filtered to a risk and country, which is why there is no
 * separate function for it.
 */
export async function fetchRiskGross(qs: string): Promise<RiskGrossResponse> {
  return apiFetch<RiskGrossResponse>(
    `/api/risk-analysis/risk-gross?${qs}`,
    {},
    API_ROOT_URL,
  );
}

/**
 * Writes a new version of the row's risk × country and closes the one in force.
 * Only the score and sources go over the wire: the ported route takes the risk
 * and country from the existing row, so a version never moves.
 */
export async function updateRiskGross(
  id: string,
  payload: RiskGrossPayload,
): Promise<void> {
  await apiFetch<void>(
    `/api/risk-analysis/risk-gross/${id}`,
    {
      method: 'PATCH',
      body: JSON.stringify({
        grossValue: payload.grossValue,
        indexes: payload.indexes,
      }),
    },
    API_ROOT_URL,
  );
}

/**
 * The port dropped `id` from the accepted body — it is server-generated now,
 * closing a bug where a caller-supplied `id` could overwrite another row's
 * primary key.
 */
export async function createRiskGross(
  payload: RiskGrossPayload,
): Promise<void> {
  await apiFetch<void>(
    '/api/risk-analysis/risk-gross',
    { method: 'POST', body: JSON.stringify(payload) },
    API_ROOT_URL,
  );
}
